// src/router/modalRoutes.ts

// Stores
import { useModalStore } from '@/store/modalStore';
import { useUserDataStore } from '@/store/userDataStore';
import { useBottomSheetStore } from '@/store/bottomSheetStore';

// Services
import { isMobileApp } from '@/services/mobile_app/mobileAppServices';
import { sportTitle, isMobile } from '@/services/admin_actions/apiUtils';
import { getPopUpsForUser } from '@/services/admin_features/popupServices';
import { hasConsented } from '@/services/consent/cookieConsentService';

// Components
import AddToAppButton from '@/components/MobileAppComponents/AddToAppButton.vue';
import UpgradeMembership from '@/components/NotificationBanners/UpgradeMembership.vue';
import CookieConsentPanel from '@/components/ComplianceComponents/CookieConsentPanel.vue';
import ConsentPopup from '@/components/ComplianceComponents/ConsentPopup.vue';
import PopupComponent from '@/components/AdminComponents/MarketingBanners/PopupComponent.vue';

// Flag to track if modal has already opened during this session
let hasShownModalThisSession = false;

// Track last visited route path
let lastPath: string | null = null;

// Timer reference for delayed modal open
let modalTimer: number | null = null;

// LocalStorage token type for dismissals
type DismissToken = { id: string; expiresAt: string };

// ----------------------------------------
// Check if a BottomSheet has been dismissed (using localStorage)
// ----------------------------------------
const isSheetDismissed = (id: string): boolean => {
  try {
    const key = `bottomsheet:${sportTitle.value.toLowerCase()}:${id}`;
    const raw = localStorage.getItem(key);
    if (!raw) return false;

    const token = JSON.parse(raw) as DismissToken;
    if (!token?.expiresAt) return false;

    return Date.now() < Date.parse(token.expiresAt);
  } catch {
    return false;
  }
};

// ----------------------------------------
// Check if a Modal has been dismissed (using localStorage)
// ----------------------------------------
const isModalDismissed = (id: string): boolean => {
  try {
    const key = `modal:${sportTitle.value.toLowerCase()}:${id}`;
    const raw = localStorage.getItem(key);
    if (!raw) return false;

    const token = JSON.parse(raw) as DismissToken;
    if (!token?.expiresAt) return false;

    return Date.now() < Date.parse(token.expiresAt);
  } catch {
    return false;
  }
};

// ----------------------------------------
// Open either a BottomSheet (mobile) or Modal (desktop)
// Only triggers if not dismissed, and sets session state
// ----------------------------------------
const openModal = (args: {
  id: string;
  title?: string;
  component: any;
  props?: Record<string, any>;
  modalOptions?: { noPadding?: boolean; whiteIcon?: boolean; persistent?: boolean };
  persistence?: {
    disablePersistence?: boolean;
    ttlDays?: number;
  };
  sheetOptions?: {
    bgColor?: 'white' | 'grey' | 'primary';
    siteLogo?: boolean;
    showOnDesktop?: boolean;
    disablePersistence?: boolean;
  };
}) => {
  const modalPersistenceDisabled = args.persistence?.disablePersistence ?? true;
  const ttlDays = args.persistence?.ttlDays ?? 30;

  // Block if dismissed
  if (isMobile.value) {
    const sheetPersistenceDisabled = args.sheetOptions?.disablePersistence ?? false;
    if (!sheetPersistenceDisabled && isSheetDismissed(args.id)) return false;
  } else {
    if (!modalPersistenceDisabled && isModalDismissed(args.id)) return false;
  }

  // Flag this session as shown
  hasShownModalThisSession = true;

  // Clear existing timer if any
  if (modalTimer) {
    window.clearTimeout(modalTimer);
    modalTimer = null;
  }

  // Schedule modal/sheet to open in 3s
  modalTimer = window.setTimeout(() => {
    if (isMobile.value) {
      const sheet = useBottomSheetStore();
      sheet.openSheet({
        id: args.id,
        title: args.title ?? '',
        component: args.component,
        props: args.props ?? {},
        options: {
          bgColor: args.sheetOptions?.bgColor ?? 'white',
          siteLogo: args.sheetOptions?.siteLogo ?? true,
          showOnDesktop: args.sheetOptions?.showOnDesktop ?? false,
          disablePersistence: args.sheetOptions?.disablePersistence ?? false,
        },
      });
      return;
    }

    const modalStore = useModalStore();
    modalStore.openModal({
      id: args.id,
      title: args.title ?? '',
      description: '',
      component: args.component,
      props: args.props ?? {},
      options: {
        noPadding: args.modalOptions?.noPadding ?? false,
        whiteIcon: args.modalOptions?.whiteIcon ?? false,
        persistent: args.modalOptions?.persistent ?? false,
        disablePersistence: modalPersistenceDisabled,
        ttlDays: ttlDays,
      },
    });
  }, 3000);

  return true;
};

// ----------------------------------------
// Check and open marketing popup (one-time per app open, called from DefaultLayout)
// ----------------------------------------
const TriggerQuizModal = async () => {
  if (hasShownModalThisSession) return;

  const userDataStore = useUserDataStore();
  if (userDataStore.triggers?.length) return;

  const modalStore = useModalStore();

  try {
    const response = await getPopUpsForUser();
    const popups = response?.data || [];

    const dismissedMap = JSON.parse(localStorage.getItem('dismissedPopups') || '{}');
    const now = new Date();

    for (const popup of popups) {
      if (popup.enabled !== 1) continue;

      const dismissedAt = dismissedMap[popup.id];
      if (dismissedAt) {
        const days = (now.getTime() - new Date(dismissedAt).getTime()) / (1000 * 60 * 60 * 24);
        if (days < 30) continue;
        delete dismissedMap[popup.id];
      }

      const current = modalStore.component?.value;
      if (current?.__name === 'QuizComponent') return;

      localStorage.setItem('dismissedPopups', JSON.stringify(dismissedMap));

      let parsedContent: any;
      try {
        parsedContent = JSON.parse(JSON.parse(popup.content));
      } catch {
        continue;
      }

      openModal({
        id: `popup-${popup.id}`,
        component: PopupComponent,
        props: { popupData: popup, popupContent: parsedContent },
        modalOptions: { noPadding: true, whiteIcon: true },
        persistence: { disablePersistence: true },
      });

      return;
    }
  } catch {}
};

// ----------------------------------------
// Show "Add to App" prompt (mobile web only)
// ----------------------------------------
const TriggerAddAppButton = async (to: any, from: any) => {
  if (hasShownModalThisSession) return;

  if (!isMobile.value) return;
  if (isMobileApp()) return;
  if (sportTitle.value.toLowerCase() !== 'squash') return;
  if (to?.path === lastPath) return;

  lastPath = to?.path ?? null;

  openModal({
    id: 'add-to-app',
    title: 'Get the Mobile App',
    component: AddToAppButton,
    sheetOptions: {
      bgColor: 'white',
      siteLogo: true,
      showOnDesktop: false,
      disablePersistence: false,
    },
  });
};

// ----------------------------------------
// Show Upgrade Membership banner (visitors/basic only)
// ----------------------------------------
const TriggerUpgradeMembership = async () => {
  if (hasShownModalThisSession) return;

  if (!isMobile.value) return;

  const userDataStore = useUserDataStore();
  const tiers = userDataStore.userInfo?.membership_tiers;

  if (!tiers) return;
  if (!tiers.is_visitor && !tiers.is_basic) return;

  openModal({
    id: 'membership-upgrade',
    title: `Enjoying ${sportTitle.value}Levels?`,
    component: UpgradeMembership,
    persistence: { disablePersistence: false, ttlDays: 30 },
    sheetOptions: {
      bgColor: 'white',
      siteLogo: true,
      showOnDesktop: false,
      disablePersistence: false,
    },
  });
};

// ----------------------------------------
// Prompt any logged-in user (any account source) who hasn't accepted the current ToS yet.
// Blocking on desktop (persistent modal, no close icon); on mobile the bottom sheet can still be
// swiped/closed, but disablePersistence keeps it from being suppressed — it reopens on next
// navigation for as long as the backend still reports consentRequired.
// ----------------------------------------
const TriggerConsentRequired = async () => {
  const userDataStore = useUserDataStore();
  if (!userDataStore.userInfo?.consentRequired) return;

  openModal({
    id: 'consent-required',
    title: 'Before you continue',
    component: ConsentPopup,
    modalOptions: { persistent: true },
    persistence: { disablePersistence: true },
    sheetOptions: {
      bgColor: 'white',
      siteLogo: true,
      showOnDesktop: false,
      disablePersistence: true,
    },
  });
};

// ----------------------------------------
// Show Cookie Cookie Consent (all users, mobile and desktop)
// ----------------------------------------
const TriggerCookieConsent = async () => {
  if (hasConsented() || isMobileApp()) return;

  openModal({
    id: 'cookie-consent',
    title: 'Cookie Preferences',
    component: CookieConsentPanel,
    persistence: { disablePersistence: true },
    sheetOptions: {
      bgColor: 'white',
      siteLogo: false,
      showOnDesktop: false,
      disablePersistence: true,
    },
  });
};

// ----------------------------------------
// Run all modal triggers sequentially (popup is excluded — handled once in DefaultLayout)
// ----------------------------------------
const RunModalRoutes = async (to: any, from: any) => {
  await TriggerConsentRequired();
  await TriggerCookieConsent();
  await TriggerAddAppButton(to, from);
  await TriggerUpgradeMembership();
};

export { RunModalRoutes, TriggerQuizModal };
